

































































import { Component, Inject, Prop, Watch, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import ListItemPageNavigation from "@/lib/components/ListItemPageNavigation.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";

import { Dictionary } from "@/lib/Dictionary.type";
import dataStore from "@/store";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";
import ErrorConverterService from "@/modules/forms/error-converter.service";
import Factory from "@/modules/factory/factory.model";
import FactoryStore from "@/modules/factory/store";
import Product from "@/modules/plushie/product.model";
import PlushieStore from "@/modules/plushie/store";

import FactoryRushCapacityEditForm from "./EditPage/EditForm.vue";
import FactoryRushCapacityViewForm from "./EditPage/ViewForm.vue";

import FactoryRushCapacitySlot from "../factory-rush-capacity-slot.model";
import FactoryRushCapacityType from "../factory-rush-capacity-type.model";
import FactoryRushCapacityStore from "../store";

@Component({
  metaInfo() {
    const component = this as FactoryRushCapacityEditPage;

    return {
      title: `${component.factoryName} Rush Slot Capacity`,
    };
  },
  components: {
    FactoryRushCapacityEditForm,
    FactoryRushCapacityViewForm,
    FormErrors,
    ListItemPageNavigation,
    LoadingSpinner,
  },
})
export default class FactoryRushCapacityEditPage extends Vue {
  @Prop({ required: true })
  public readonly factoryId!: string;

  @Prop({ default: () => ({}) })
  public readonly query!: Dictionary<unknown>;

  @Prop({ default: () => ({}) })
  public readonly listQuery?: Dictionary<string[]>;

  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  @Inject("ErrorConverterService")
  private fErrorConverterService!: ErrorConverterService;

  private fErrorsList: string[] = [];
  private fFactoryDataStore: FactoryStore;
  private fFactoryRushCapacityStore: FactoryRushCapacityStore;
  private fIsLoading = false;
  private fIsModeTogglePending = false;
  private fPlushieStore: PlushieStore;

  get canAccessFactoryRushCapacity(): boolean {
    return this.canManageFactoryRushCapacity || this.canViewFactoryRushCapacity;
  }

  get canManageFactoryRushCapacity(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.FACTORY_RUSH_CAPACITY_MANAGE);
  }

  get canViewFactoryRushCapacity(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.FACTORY_RUSH_CAPACITY_VIEW);
  }

  get factory(): Factory | undefined {
    return this.fFactoryDataStore.getFactoryById(this.factoryId);
  }

  get factoryName(): string {
    return this.factory ? this.factory.name : "Factory";
  }

  get configuredSlots(): FactoryRushCapacitySlot[] {
    return this.fFactoryRushCapacityStore.getFactoryRushCapacitySlotsByFactoryId(
      this.factoryId
    );
  }

  get errorsList(): string[] {
    return this.fErrorsList;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get isModeTogglePending(): boolean {
    return this.fIsModeTogglePending;
  }

  get isModeEnabled(): boolean {
    return this.fFactoryRushCapacityStore.getFactoryRushCapacityModeById(
      this.factoryId
    );
  }

  get products(): Product[] {
    const productsById: Dictionary<Product> = {};

    this.fPlushieStore.activeProductsList.forEach((product) => {
      productsById[product.id] = product;
    });

    this.configuredSlots.forEach((slot) => {
      const product = this.fPlushieStore.getProductById(slot.product);

      if (product) {
        productsById[product.id] = product;
      }
    });

    return Object.values(productsById).sort((left, right) =>
      left.name.toLowerCase().localeCompare(right.name.toLowerCase())
    );
  }

  get showDisabledHint(): boolean {
    return !this.isModeEnabled && this.canAccessFactoryRushCapacity;
  }

  get showEditForm(): boolean {
    return this.canManageFactoryRushCapacity && this.isModeEnabled;
  }

  get showViewForm(): boolean {
    return this.canViewFactoryRushCapacity && !this.showEditForm;
  }

  get types(): FactoryRushCapacityType[] {
    return this.fFactoryRushCapacityStore.factoryRushCapacityTypesList;
  }

  constructor() {
    super();

    this.fFactoryDataStore = getModule(FactoryStore, dataStore);
    this.fFactoryRushCapacityStore = getModule(
      FactoryRushCapacityStore,
      dataStore
    );
    this.fPlushieStore = getModule(PlushieStore, dataStore);
  }

  public async getFactoriesOrder(): Promise<string[]> {
    await this.fFactoryDataStore.loadFactories();

    return this.fFactoryDataStore.factoriesList.map((factory) => factory.id);
  }

  public async toggleFactoryRushCapacityMode(): Promise<void> {
    if (this.fIsModeTogglePending) {
      return;
    }

    this.fErrorsList = [];
    this.fIsModeTogglePending = true;

    try {
      if (this.isModeEnabled) {
        await this.fFactoryRushCapacityStore.disableFactoryRushCapacityModeById(
          this.factoryId
        );
      } else {
        await this.fFactoryRushCapacityStore.enableFactoryRushCapacityModeById(
          this.factoryId
        );
      }
    } catch (e) {
      this.fErrorsList = this.fErrorsList.concat(
        this.fErrorConverterService.describeError(e)
      );
    } finally {
      this.fIsModeTogglePending = false;
    }
  }

  private async getFactoryData(factoryId: string): Promise<void> {
    const loadRequests: Array<Promise<unknown>> = [
      this.fFactoryDataStore.loadFactoryById({
        id: factoryId,
        useCache: false,
      }),
      this.fFactoryRushCapacityStore.loadFactoryRushCapacityModeById({
        id: factoryId,
        useCache: false,
      }),
    ];

    if (this.canAccessFactoryRushCapacity) {
      loadRequests.push(this.loadMatrixData(factoryId));
    }

    await Promise.all(loadRequests);
  }

  private async loadMatrixData(factoryId: string): Promise<void> {
    await Promise.all([
      this.fFactoryRushCapacityStore.loadFactoryRushCapacityTypes({
        useCache: true,
      }),
      this.fFactoryRushCapacityStore.loadFactoryRushCapacitySlotsByFactoryId({
        factoryId,
        useCache: false,
      }),
      this.fPlushieStore.loadProducts(),
    ]);
  }

  @Watch("factoryId", { immediate: true })
  private async _onFactoryIdChange() {
    if (this.factoryId == null) {
      return;
    }

    this.fIsLoading = true;

    try {
      await this.getFactoryData(this.factoryId);
      this.fIsLoading = false;
    } catch (e) {
      this.fIsLoading = false;
      throw e;
    }
  }
}
